import { useState, type FC } from "react";
import { useSelector } from "@legendapp/state/react";
import { FilterStore$, type DatePreset } from "@/stores/filterStore";
import DateRangePicker from "@/layouts/AppLayout/DateRangePicker";
import {
  formatStoredRangeLabel,
  toISODate,
} from "@/layouts/AppLayout/dateRangePresets";
import useClickOutside from "@/hooks/useClickOutside";
import { TEXT } from "@/constants/text";
import styles from "@/layouts/AppLayout/DatePresetTabs.module.css";

const PRESETS: { value: DatePreset; label: string }[] = [
  { value: "day", label: TEXT.datePresetTabs.day },
  { value: "week", label: TEXT.datePresetTabs.week },
  { value: "month", label: TEXT.datePresetTabs.month },
  { value: "custom", label: TEXT.datePresetTabs.custom },
];

const DatePresetTabs: FC = () => {
  const activePreset = useSelector(() => FilterStore$.datePreset.get());
  const dateRange = useSelector(() => FilterStore$.dateRange.get());
  const resolvedRange = useSelector(() => FilterStore$.resolvedDateRange.get());
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const rootRef = useClickOutside<HTMLDivElement>(isPickerOpen, () =>
    setIsPickerOpen(false)
  );

  // "Custom" opens the picker rather than committing immediately — Apply is
  // what writes to the store, so a cancelled or dismissed picker leaves
  // whichever preset was active before untouched.
  const onPresetClick = (preset: DatePreset) => {
    if (preset === "custom") {
      setIsPickerOpen(true);
      return;
    }
    setIsPickerOpen(false);
    FilterStore$.datePreset.set(preset);
  };

  const onApply = (range: { start: Date; end: Date }) => {
    FilterStore$.assign({
      dateRange: { start: toISODate(range.start), end: toISODate(range.end) },
      datePreset: "custom",
      // Drop the previous preset's range so the label shows the picked one
      // until the page re-resolves (and clamps) it.
      resolvedDateRange: { end: null, start: null },
    });
    setIsPickerOpen(false);
  };

  // Custom falls back to the picked range until the page resolves one; the
  // other presets have nothing to show until then.
  const rangeLabel = formatStoredRangeLabel(
    resolvedRange,
    activePreset === "custom" ? dateRange : null
  );

  // While the picker is open, highlight "Custom" immediately — the store isn't
  // written until Apply fires, so activePreset still reflects the prior preset.
  const displayPreset = isPickerOpen ? "custom" : activePreset;

  return (
    <div ref={rootRef} className={styles.root}>
      <div className={styles.tabs}>
        {PRESETS.map((preset) => (
          <button
            key={preset.value}
            className={
              preset.value === displayPreset
                ? `${styles.tab} ${styles.active}`
                : styles.tab
            }
            type="button"
            onClick={() => onPresetClick(preset.value)}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <span className={styles.rangeLabel}>{rangeLabel}</span>
      {isPickerOpen && (
        <DateRangePicker
          initialRange={dateRange}
          onApply={onApply}
          onCancel={() => setIsPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default DatePresetTabs;
